import React, { memo, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import SelectCustom from '../../../components/atoms/select/SelectCustom'
import CheckBoxCustom from '../../../components/atoms/checkbox/CheckBox'
import { Box, CardMedia, Typography, IconButton } from '@mui/material'
import { formatCommonAmount } from '../../../util/formatAmount'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import styles from './Package.module.scss'
import InputCustom from '../../../components/atoms/input/InputCustom'
import { toastHelper } from '../../../util/toastHelper'
import { truncateText } from '../../../util'

const PackageSelection = ({
	selectedPackage,
	setSelectedPackage,
	setSelectedKazoo = () => {},
}) => {
	const reduxPackages = useSelector((state) => state.packages.packages)
	useEffect(() => {
		if (!!selectedPackage?._id) {
			const pkg = reduxPackages?.find(
				(pkg) => pkg._id === selectedPackage?._id
			)
			const addOnsIds = pkg?.addOnsIds
			const selectedAddOns = selectedPackage?.addOnsIds?.map(
				(addon) => addon?._id
			)
			if (!!pkg && addOnsIds?.length !== selectedAddOns?.length) {
				const remainingAddOns = addOnsIds?.filter((addon) => {
					if (!selectedAddOns?.includes(addon?._id)) return addon
				})
				setSelectedPackage((prev) => ({
					...prev,
					addOnsIds: [...prev?.addOnsIds, ...remainingAddOns],
				}))
			}
		}
	}, [reduxPackages])
	const packageOptions = useMemo(() => {
		const options = !reduxPackages?.length
			? []
			: reduxPackages.map((pkg) => ({
					label: pkg.name + ' - ' + formatCommonAmount(pkg.price),
					value: pkg._id,
				}))
		return [...options, { label: 'None', value: 'none' }]
	}, [reduxPackages])

	const handlePackageSelect = (id) => {
		if (id === 'none' || !id) {
			setSelectedPackage(null)
			return
		}
		if (id) {
			const pkg = reduxPackages.find((pkg) => pkg._id === id)
			const selectedAddOns =
				pkg?.addOnsIds?.flatMap((addon) =>
					addon?.isThisAddOnIsRequired ? [addon._id] : []
				) ?? []
			setSelectedPackage({ ...selectedPackage, ...pkg, selectedAddOns })
			setSelectedKazoo?.(
				(prev) => prev && { ...(prev || {}), userBudget: null }
			)
		}
	}

	const handleAddonSelect = (id) => {
		setSelectedPackage((prev) => {
			const addon = prev.addOnsIds.find((a) => a._id === id)

			if (
				addon?.isThisAddOnIsRequired &&
				prev.selectedAddOns.includes(id)
			)
				return prev

			const selectedAddOns = prev.selectedAddOns.includes(id)
				? prev.selectedAddOns.filter((addonId) => addonId !== id)
				: [...prev.selectedAddOns, id]

			return { ...prev, selectedAddOns }
		})
	}

	const handleQuantityChange = (addon, newQuantity) => {
		if (newQuantity < 1 || newQuantity > 99999) return
		else if (!!addon?.minQuantity && newQuantity < addon?.minQuantity)
			return toastHelper(
				`Minimum quantity for ${addon?.name} is ${addon?.minQuantity}`,
				'error'
			)
		else if (!!addon?.maxQuantity && newQuantity > addon?.maxQuantity)
			return toastHelper(
				`Maximum quantity for ${addon?.name} is ${addon?.maxQuantity}`,
				'error'
			)
		setSelectedPackage((prev) => {
			const selectedAddOns = [...prev.selectedAddOns]
			if (!selectedAddOns.includes(addon._id))
				selectedAddOns.push(addon._id)
			const addOnsIds = prev.addOnsIds.map((a) =>
				a._id === addon._id ? { ...a, quantity: newQuantity } : a
			)
			return { ...prev, selectedAddOns, addOnsIds }
		})
	}

	const addonCard = (addon) => {
		const isChecked = selectedPackage?.selectedAddOns?.includes(addon._id)
		return (
			<Box
				key={addon?._id}
				className={styles.packageAddonsItem}
				sx={{
					backgroundColor:
						addon?.isThisAddOnIsRequired || isChecked
							? 'var(--bg3)'
							: 'var(--white)',
					border:
						addon?.isThisAddOnIsRequired || isChecked
							? '1px solid #7CA6BD'
							: '1px solid #DFE4EB',
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'start',
						gap: '12px',
					}}
				>
					<CheckBoxCustom
						size="18px"
						checked={isChecked}
						onChange={() => handleAddonSelect(addon._id)}
					/>
					<CardMedia
						component="img"
						sx={{
							width: '42px',
							height: '42px',
							objectFit: 'cover',
							borderRadius: '8px',
							flexShrink: 0,
						}}
						image={addon?.image || '/images/placeholder.png'}
						alt={addon.name}
						loading="lazy"
					/>
					<div className={styles.packageAddonsItemNamemain}>
						<Typography
							variant="body2"
							className={styles.packageAddonsItemName}
							title={addon?.name || '--'}
						>
							{truncateText(addon?.name)}
						</Typography>
						<Typography
							variant="body2"
							className={styles.packageDescription}
							title={addon?.description || '--'}
						>
							{truncateText(addon?.description)}
						</Typography>
						<Typography variant="body2">
							<span className={styles.packageAddonsItemPrice}>
								{formatCommonAmount(addon?.price)}
							</span>
							{/* {isChecked && !!addon?.minQuantity && addon?.minQuantity > 1
								? ` (min.: ${parseInt(addon?.minQuantity)}` : ''}
							{isChecked && !!addon?.maxQuantity && addon?.maxQuantity > 1
								? ` - max.: ${parseInt(addon?.maxQuantity)})` : ''} */}
						</Typography>
					</div>
				</div>
				<div>
					{addon?.isThisAddOnIsRequired && (
						<Box className={styles.packageAddonsItemRequired}>
							Required
						</Box>
					)}
					<div className={styles.quantityDiv}>
						<IconButton
							size="small"
							title={
								addon.quantity > (addon.minQuantity || 1)
									? 'Remove'
									: 'Minimum quantity reached'
							}
							disabled={
								!isChecked &&
								addon.quantity <= (addon.minQuantity || 1)
							}
							onClick={() =>
								handleQuantityChange(
									addon,
									(addon.quantity || addon.minQuantity || 1) -
										1
								)
							}
						>
							<RemoveIcon fontSize="small" />
						</IconButton>
						<InputCustom
							type="number"
							min={addon.minQuantity || 1}
							value={addon.quantity || addon.minQuantity || 1}
							onChange={(e) => {
								const val = parseInt(e.target.value)
								if (!isNaN(val))
									handleQuantityChange(addon, val)
							}}
							style={{
								width:
									(addon.quantity || addon.minQuantity || 1) >
									99
										? '70px'
										: '40px',
							}}
							sx={{
								'& .MuiInputBase-root': {
									borderRadius: 0,
									minHeight: '32px !important',
								},
								'& .MuiOutlinedInput-notchedOutline': {
									border: 'none',
								},
								'& .MuiInputBase-input': {
									textAlign: 'center',
									padding: '0 !important',
									height: '32px',
								},
							}}
						/>
						<IconButton
							size="small"
							title={
								addon.quantity < (addon.maxQuantity || 1)
									? 'Add'
									: 'Maximum quantity reached'
							}
							disabled={
								!isChecked &&
								addon.quantity >= (addon.maxQuantity || 1)
							}
							onClick={() =>
								handleQuantityChange(
									addon,
									(addon.quantity || addon.minQuantity || 1) +
										1
								)
							}
						>
							<AddIcon fontSize="small" />
						</IconButton>
					</div>
				</div>
			</Box>
		)
	}
	const renderAddons = (addOnsIds, flag) => {
		if (addOnsIds?.length === 0) return <></>
		return (
			<div className={styles.packageSelection}>
				<label htmlFor="Add-ons" className={styles.label}>
					{flag ? 'Required Add-ons' : 'Optional Add-ons'}
				</label>
				<Box className={styles.packageAddonsList}>
					{addOnsIds?.map((addon) => addonCard(addon))}
				</Box>
			</div>
		)
	}
	const hasAddons = selectedPackage?.addOnsIds?.length > 0
	return (
		<>
			<div className={styles.packageSelection}>
				<label htmlFor="Select Package" className={styles.label}>
					Select Package
				</label>
				<SelectCustom
					options={packageOptions}
					renderValueAsLabel={true}
					defaultValue={selectedPackage?._id || 'none'}
					setDataFunction={({ value }) => handlePackageSelect(value)}
				/>
			</div>

			{selectedPackage && (
				<>
					<div className={styles.packageSelection}>
						<p className={styles.packageDescription}>
							{selectedPackage?.description || '--'}
						</p>
					</div>
					{hasAddons && (
						<div className={styles.packageSelection}>
							<Box className={styles.packageAddonsList}>
								{renderAddons(
									selectedPackage?.addOnsIds?.filter(
										(addon) => addon?.isThisAddOnIsRequired
									),
									true
								)}
								{renderAddons(
									selectedPackage?.addOnsIds?.filter(
										(addon) => !addon?.isThisAddOnIsRequired
									),
									false
								)}
							</Box>
						</div>
					)}
				</>
			)}
		</>
	)
}

export default memo(PackageSelection)
